import { Injectable } from '@angular/core';
import {
  HttpInterceptor, HttpRequest, HttpHandler, HttpEvent, HttpErrorResponse
} from '@angular/common/http';
import { BehaviorSubject, Observable, throwError } from 'rxjs';
import { catchError, filter, switchMap, take } from 'rxjs/operators';
import { AuthService } from 'src/app/___LOGIC/__auth/auth.service';

@Injectable()
export class AuthInterceptor implements HttpInterceptor {

  private refreshing = false;
  private refreshSubject = new BehaviorSubject<string | null>(null);

  constructor(private auth: AuthService) {}

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    // Skip auth decoration on auth endpoints to avoid loops
    if (this.isAuthEndpoint(req.url)) {
      return next.handle(this.ensureClient(req));
    }

    const withAuth = this.attachAuth(this.ensureClient(req), this.auth.token);
    
    return next.handle(withAuth).pipe(
      catchError((err: HttpErrorResponse) => {
        if (err.status === 401) {
          return this.handle401(req, next);
        }
        return throwError(() => err);
      })
    );
  }

  /** Add Authorization if we have a token */
  private attachAuth(req: HttpRequest<any>, token: string | null): HttpRequest<any> {
    if (!token) return req;
    return req.clone({
      setHeaders: { Authorization: `Bearer ${token}` }
    });
  }

  /** Ensure x-client header is present; default to 'website' if not set */
  private ensureClient(req: HttpRequest<any>): HttpRequest<any> {
    if (req.headers.has('x-client')) return req;
    return req.clone({ setHeaders: { 'x-client': 'website' } });
  }

  /** Centralized 401 handling with refresh queueing */
  private handle401(originalReq: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {

    debugger
    if (!this.refreshing) {
      this.refreshing = true;
      this.refreshSubject.next(null);

      return this.auth.refresh().pipe(
        switchMap(({ access_token }) => {
          // store the new token
          this.auth.setToken(access_token);
          this.refreshing = false;
          this.refreshSubject.next(access_token);

          // retry the original request with new token (preserve x-client if set)
          const retried = this.attachAuth(this.ensureClient(originalReq), access_token);
          return next.handle(retried);
        }),
        catchError(err => {
          this.refreshing = false;
          this.refreshSubject.next(null);
          // hard logout / clear session
          this.auth.setToken(null);
          return throwError(() => err);
        })
      );
    } else {
      // A refresh is already in progress: wait for it and then retry
      return this.refreshSubject.pipe(
        filter((t): t is string => t !== null),
        take(1),
        switchMap((token) => next.handle(this.attachAuth(this.ensureClient(originalReq), token)))
      );
    }
  }

  private isAuthEndpoint(url: string): boolean {
    // Works with relative or absolute URLs
    return /\/auth\/(login|refresh|logout)\b/i.test(url);
  }
}
